import { useState, type FormEvent } from 'react';
import { Button, Group, Modal, Select, Stack, Text, TextInput } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '@/api/errors';
import { registerObject, useKinds, type DataObject } from '@/api/objects';
import { brand } from '@/theme/theme';
import { objectName } from './objectName';
import { useKindLabel } from './useKindLabel';

export interface RegisterObjectModalProps {
  projectId: string;
  opened: boolean;
  onClose: () => void;
  /** Existing objects; the "replaces" picker offers those of the chosen kind. */
  existing: DataObject[];
  /** After registering, go straight to the upload page (default) or stay. */
  goToUpload?: boolean;
  /** Kind preselected from the checklist. */
  presetKind?: string | null;
  /** Staff may register every kind, derived-only ones included. */
  allKinds?: boolean;
}

export function RegisterObjectModal({ projectId, opened, onClose, existing, goToUpload = true, presetKind = null, allKinds = false }: RegisterObjectModalProps) {
  const { t, i18n } = useTranslation('projects');
  const { t: tc } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: kinds } = useKinds();
  const kindLabel = useKindLabel();
  const [kind, setKind] = useState<string | null>(presetKind);
  const [name, setName] = useState('');
  const [replaces, setReplaces] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const register = useMutation({
    mutationFn: () => registerObject(projectId, { kind: kind as never, name: name.trim(), notes, replaces: replaces ?? undefined }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['objects', projectId] });
      void qc.invalidateQueries({ queryKey: ['project', projectId] });
      void qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });

  const handleClose = () => {
    setKind(null);
    setName('');
    setReplaces(null);
    setNotes('');
    setError(null);
    onClose();
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!kind || !name.trim()) return;
    setError(null);
    try {
      const obj = await register.mutateAsync();
      handleClose();
      if (goToUpload) await navigate({ to: '/projects/$projectId/upload/$objectId', params: { projectId, objectId: obj.public_id } });
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const uploadable = (kinds ?? []).filter((k) => allKinds || k.client_uploadable);
  const sameKind = existing.filter((o) => o.kind === kind && !o.deleted);
  const packed = kinds?.find((k) => k.kind === kind)?.arrives_packed;

  return (
    <Modal opened={opened} onClose={handleClose} title={t('objects.registerTitle')}>
      <form onSubmit={(e) => void handleSubmit(e)}>
        <Stack gap="md">
          <Select
            label={t('objects.kind')}
            data={uploadable.map((k) => ({ value: k.kind, label: k.label[i18n.language] ?? k.kind }))}
            value={kind}
            onChange={(v) => {
              setKind(v);
              setReplaces(null);
            }}
            required
            searchable
          />
          {packed && (
            <Text fz={12} c={brand.textMuted}>
              {t('objects.packedHint')}
            </Text>
          )}
          {sameKind.length > 0 && (
            <Select
              label={t('objects.replaces')}
              data={[
                { value: '', label: t('objects.replacesNone') },
                ...sameKind.map((o) => ({ value: o.public_id, label: `${kindLabel(o.kind)} · ${objectName(o)}` })),
              ]}
              value={replaces ?? ''}
              onChange={(v) => setReplaces(v || null)}
            />
          )}
          <TextInput label={t('objects.name')} description={t('objects.nameHint')} value={name} onChange={(e) => setName(e.currentTarget.value)} maxLength={255} required data-autofocus />
          <TextInput label={t('objects.note')} value={notes} onChange={(e) => setNotes(e.currentTarget.value)} maxLength={500} />
          {error && (
            <Text c="red" role="alert">
              {error}
            </Text>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={handleClose}>
              {tc('common.cancel')}
            </Button>
            <Button type="submit" loading={register.isPending} disabled={!kind || !name.trim()}>
              {t('objects.register')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
